import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

// 常量定義：結案後錄影自動停止的倒數秒數
const VIDEO_AUTO_STOP_COUNTDOWN_SECONDS = 60;

export interface VideoStopCountdownContextValue {
  countdown: number | null;
  isCountingDown: boolean;
  startCountdown: (onFinish: () => void) => void;
  stopCountdown: () => void;
  resetCountdown: () => void;
}

const VideoStopCountdownContext = createContext<VideoStopCountdownContextValue | null>(null);

interface VideoStopCountdownProviderProps {
  children: React.ReactNode;
}

/**
 * VideoStopCountdownProvider
 * 管理 QC 完成後的錄影自動停止倒數功能
 */
export const VideoStopCountdownProvider: React.FC<VideoStopCountdownProviderProps> = ({ children }) => {
  const [ countdown, setCountdown ] = useState<number | null>(null); // 倒數秒數
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null); // 計時器
  const startedRef = useRef(false); // 是否已啟動倒數（防止重複）
  const onCountdownFinishRef = useRef<(() => void) | null>(null); // 倒數完成時的回調函數

  /**
   * 開始倒數
   * @param onFinish - 倒數完成時的回調函數
   */
  const startCountdown = (onFinish: () => void): void => {
    // 如果已經在倒數中，跳過
    if (startedRef.current) {
      return;
    }

    console.log('[VideoStopCountdown] Starting countdown');

    // 標記已啟動
    startedRef.current = true;

    // 保存回調函數
    onCountdownFinishRef.current = onFinish;

    // 初始化倒數
    setCountdown(VIDEO_AUTO_STOP_COUNTDOWN_SECONDS);

    let secondsRemaining = VIDEO_AUTO_STOP_COUNTDOWN_SECONDS;

    // 每秒更新倒數
    timerRef.current = setInterval(() => {
      secondsRemaining -= 1;

      // 倒數結束
      if (secondsRemaining <= 0) {
        console.log('[VideoStopCountdown] Countdown finished');

        // 清除計時器
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        setCountdown(null);

        // 執行回調函數
        if (onCountdownFinishRef.current) {
          onCountdownFinishRef.current();
        }
      } else {
        // 更新倒數狀態
        setCountdown(secondsRemaining);
      }
    }, 1000);
  };

  /**
   * 停止倒數
   */
  const stopCountdown = (): void => {
    // 清除計時器
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // 重置狀態
    setCountdown(null);
    startedRef.current = false;
    onCountdownFinishRef.current = null;
  };

  /**
   * 重置倒數（用於離開 COMPLETED 狀態）
   */
  const resetCountdown = (): void => {
    stopCountdown();
  };

  // 組件卸載時清理
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const value: VideoStopCountdownContextValue = {
    countdown, // 當前倒數秒數（null 表示未倒數）
    isCountingDown: countdown !== null, // 是否正在倒數
    startCountdown, // 開始倒數
    stopCountdown, // 停止倒數
    resetCountdown, // 重置倒數
  };

  return (
    <VideoStopCountdownContext.Provider value={value}>
      {children}
    </VideoStopCountdownContext.Provider>
  );
};

/**
 * useVideoStopCountdown Hook
 * 使用錄影倒數功能
 */
export function useVideoStopCountdown(): VideoStopCountdownContextValue {
  const context = useContext(VideoStopCountdownContext);

  if (!context) {
    throw new Error('useVideoStopCountdown must be used within VideoStopCountdownProvider');
  }

  return context;
}
